import styled from 'styled-components';
import { CSSProperties, ComponentPropsWithoutRef, forwardRef } from 'react';

import { Theme } from '@/shared/themes';
import { addRem } from '@/shared/styles';
import { useTheme } from '@/services/theme';

interface BoxOptions {
  position?: CSSProperties['position'];
  fontSize?: string | number;
  fontFamily?: 'body' | 'heading' | 'gramatika' | 'helvetica';
  textAlign?: CSSProperties['textAlign'];
  color?: keyof Theme['colors'] | keyof Theme['colors']['text'] | CSSProperties['color'];
  background?: keyof Theme['colors'] | CSSProperties['background'];
  borderRadius?: CSSProperties['borderRadius'];
  border?: CSSProperties['border'];
  borderTop?: CSSProperties['borderTop'];
  borderBottom?: CSSProperties['borderBottom'];
  maxWidth?: CSSProperties['maxWidth'];
  width?: CSSProperties['width'];
  maxHeight?: CSSProperties['maxHeight'];
  height?: CSSProperties['height'];
  padding?: CSSProperties['padding'];
  px?: string | number;
  py?: string | number;
  pt?: string | number;
  pr?: string | number;
  pb?: string | number;
  pl?: string | number;
  margin?: CSSProperties['margin'];
  mx?: string | number;
  my?: string | number;
  mt?: string | number;
  mr?: string | number;
  mb?: string | number;
  ml?: string | number;
  transition?: CSSProperties['transition'];
  zIndex?: CSSProperties['zIndex'];
  overflow?: CSSProperties['overflow'];
  disabled?: boolean;
}

const StyledBox = styled.div<BoxOptions>`
  /* font */
  ${({ fontSize }: BoxOptions) => fontSize !== undefined && `font-size: ${addRem(fontSize)};`}
  ${({ fontFamily, theme }: BoxOptions & { theme: Theme }) =>
    fontFamily !== undefined && `font-family: ${theme.fonts[fontFamily] || fontFamily};`}
  ${({ textAlign }: BoxOptions) => textAlign !== undefined && `text-align: ${textAlign};`}
    ${({ color, theme }: BoxOptions & { theme: Theme }) =>
    color !== undefined &&
    `color: ${
      theme.colors.text[color as keyof Theme['colors']['text']] ||
      theme.colors[color as keyof Theme['colors']] ||
      color
    };`}

    ${({ background, theme }: BoxOptions & { theme: Theme }) =>
    background !== undefined &&
    `background: ${theme.colors[background as keyof Theme['colors']] || background};`}
    ${({ borderRadius }: BoxOptions) =>
    borderRadius !== undefined && `border-radius: ${addRem(borderRadius)};`}
    ${({ border }: BoxOptions) => border !== undefined && `border: ${border};`}
    ${({ borderBottom }: BoxOptions) =>
    borderBottom !== undefined && `border-bottom: ${borderBottom};`}
    ${({ borderTop }: BoxOptions) => borderTop !== undefined && `border-top: ${borderTop};`}

    /* dimensions */
    ${({ maxWidth }: BoxOptions) => maxWidth !== undefined && `max-width: ${addRem(maxWidth)};`}
    ${({ width }: BoxOptions) => width !== undefined && `width: ${addRem(width)};`}
    ${({ maxHeight }: BoxOptions) => maxHeight !== undefined && `max-height: ${addRem(maxHeight)};`}
    ${({ height }: BoxOptions) => height !== undefined && `height: ${addRem(height)};`}

    /* padding */
    ${({ padding }: BoxOptions) => padding !== undefined && `padding: ${addRem(padding)};`}
    ${({ px }: BoxOptions) => px && `padding-left: ${addRem(px)}; padding-right: ${addRem(px)};`}
    ${({ py }: BoxOptions) => py && `padding-top: ${addRem(py)}; padding-bottom: ${addRem(py)};`}
    ${({ pt }: BoxOptions) => pt && `padding-top: ${addRem(pt)};`}
    ${({ pr }: BoxOptions) => pr && `padding-right: ${addRem(pr)};`}
    ${({ pb }: BoxOptions) => pb && `padding-bottom: ${addRem(pb)};`}
    ${({ pl }: BoxOptions) => pl && `padding-left: ${addRem(pl)};`}

    /* margin */
    ${({ margin }: BoxOptions) => margin !== undefined && `margin: ${margin};`}
    ${({ mx }: BoxOptions) => mx && `margin-left: ${addRem(mx)}; margin-right: ${addRem(mx)};`}
    ${({ my }: BoxOptions) => my && `margin-top: ${addRem(my)}; margin-bottom: ${addRem(my)};`}
    ${({ mt }: BoxOptions) => mt && `margin-top: ${addRem(mt)};`}
    ${({ mr }: BoxOptions) => mr && `margin-right: ${addRem(mr)};`}
    ${({ mb }: BoxOptions) => mb && `margin-bottom: ${addRem(mb)};`}
    ${({ ml }: BoxOptions) => ml && `margin-left: ${addRem(ml)};`}

    ${({ position }: BoxOptions) => position !== undefined && `position: ${position};`}
    ${({ transition }: BoxOptions) => transition !== undefined && `transition: ${transition};`}
    ${({ zIndex }: BoxOptions) => zIndex !== undefined && `z-index: ${zIndex};`}
    ${({ overflow }: BoxOptions) => overflow !== undefined && `overflow: ${overflow};`}
`;

export interface BoxProps extends Omit<ComponentPropsWithoutRef<'div'>, 'color'>, BoxOptions {}

export const Box = forwardRef<HTMLDivElement, BoxProps>(function Box(props, ref) {
  const { theme } = useTheme();

  return <StyledBox ref={ref} theme={theme} {...props} />;
});

Box.displayName = 'Box';
